import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { DashboardLayout as RoleBasedDashboardLayout } from '@/layouts/DashboardLayout';
import { DashboardRedirect } from '@/pages/auth/DashboardRedirect';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import { ThemeSlot } from '@/themes/ThemeSlot';
import { DashboardThemeSlot } from '@/themes/DashboardThemeSlot';
import { CreatePageShellSlot, CreatePageSlot } from '@/components/common/CreatePageSlot';

// Guest-facing + auth pages/layouts, and every page for the themed tier (Guest, Org Admin,
// Org Manager, Org Staff), are resolved from the active structural theme (ADR Section 16,
// ADR-2026-07-D) — see ThemeSlot/DashboardThemeSlot and frontend/src/themes/. The fixed tier
// (Super Admin, Platform Admin, Platform Staff) below is NOT themed — same shared UI regardless
// of `active_theme`.

// Fixed-tier shared pages (unchanged by ADR-2026-07-D — see note above)
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SuperAdminDashboard = lazy(() => import('@/pages/dashboard/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));

// Admin pages
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// SettingsPageOrgAdmin consolidated into universal /settings page

// PLATFORM_ADMIN pages
const PlatformAdminDashboard2 = lazy(() => import('@/pages/admin/PlatformAdmin/PlatformAdminDashboard').then(m => ({ default: m.PlatformAdminDashboard })));
const OrganizationsPage = lazy(() => import('@/pages/admin/PlatformAdmin/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const DisputesPage = lazy(() => import('@/pages/admin/PlatformAdmin/DisputesPage').then(m => ({ default: m.DisputesPage })));
const UsersPage = lazy(() => import('@/pages/admin/PlatformAdmin/UsersPage').then(m => ({ default: m.UsersPage })));
const SecurityPage = lazy(() => import('@/pages/admin/PlatformAdmin/SecurityPage').then(m => ({ default: m.SecurityPage })));
const PaymentPage = lazy(() => import('@/pages/admin/PlatformAdmin/PaymentPage').then(m => ({ default: m.PaymentPage })));
const CompliancePagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/CompliancePage').then(m => ({ default: m.CompliancePage })));
const PropertiesPagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/PropertiesPagePlatform').then(m => ({ default: m.PropertiesPagePlatform })));
const BookingsPagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/BookingsPagePlatform').then(m => ({ default: m.BookingsPagePlatform })));
const AuditLogPagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/AuditLogPagePlatform').then(m => ({ default: m.AuditLogPagePlatform })));
const ReportsPagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/ReportsPagePlatform').then(m => ({ default: m.ReportsPagePlatform })));
// SettingsPagePlatform consolidated into universal /settings page
const ListingReviewPage = lazy(() => import('@/pages/admin/PlatformAdmin/ListingReviewPage').then(m => ({ default: m.ListingReviewPage })));
const ExperienceReviewPage = lazy(() => import('@/pages/admin/PlatformAdmin/ExperienceReviewPage').then(m => ({ default: m.ExperienceReviewPage })));
const PlatformStaffPage = lazy(() => import('@/pages/admin/PlatformAdmin/PlatformStaffPage').then(m => ({ default: m.PlatformStaffPage })));
const AppearancePagePlatform = lazy(() => import('@/pages/admin/PlatformAdmin/AppearancePage').then(m => ({ default: m.AppearancePage })));
const ChannelManagerProvidersPage = lazy(() => import('@/pages/admin/PlatformAdmin/ChannelManagerProvidersPage').then(m => ({ default: m.ChannelManagerProvidersPage })));

// PLATFORM_STAFF pages
const PlatformStaffDashboard = lazy(() => import('@/pages/admin/PlatformStaff/PlatformStaffDashboard').then(m => ({ default: m.PlatformStaffDashboard })));
const StaffOrganizationsPage = lazy(() => import('@/pages/admin/PlatformStaff/StaffOrganizationsPage').then(m => ({ default: m.StaffOrganizationsPage })));
const StaffOrgDetailPage = lazy(() => import('@/pages/admin/PlatformStaff/StaffOrgDetailPage').then(m => ({ default: m.StaffOrgDetailPage })));

// SUPER_ADMIN pages
const SystemSettingsPage = lazy(() => import('@/pages/admin/SuperAdmin/SystemSettingsPage').then(m => ({ default: m.SystemSettingsPage })));
const FeatureFlagsPage = lazy(() => import('@/pages/admin/SuperAdmin/FeatureFlagsPage').then(m => ({ default: m.FeatureFlagsPage })));
const DebugPage = lazy(() => import('@/pages/admin/SuperAdmin/DebugPage').then(m => ({ default: m.DebugPage })));
const BackupPage = lazy(() => import('@/pages/admin/SuperAdmin/BackupPage').then(m => ({ default: m.BackupPage })));

const BookingDetailPage = lazy(() => import('@/pages/dashboard/BookingDetailPage'));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen text="Loading..." />}>
      <Routes>
        {/* Public routes with guest layout — pages/layout resolved from the active theme */}
        <Route element={<ThemeSlot page="GuestLayout" />}>
          <Route path="/" element={<ThemeSlot page="HomePage" />} />
          <Route path="/search" element={<ThemeSlot page="SearchResultsPage" />} />
          <Route path="/properties/:id" element={<ThemeSlot page="PropertyDetailPage" />} />
          {/* Booking requires an account (see requireVerified.ts on booking-service for the
              server-side verified-email-or-phone gate enforced once authenticated) -- browsing,
              search, and itinerary planning stay public. */}
          <Route
            path="/booking/:propertyId"
            element={
              <ProtectedRoute>
                <ThemeSlot page="BookingFlowPage" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking/confirmation/:bookingId"
            element={
              <ProtectedRoute>
                <ThemeSlot page="BookingConfirmationPage" />
              </ProtectedRoute>
            }
          />
          <Route path="/experiences" element={<ThemeSlot page="ExperienceBrowsePage" />} />
          <Route path="/experiences/:id" element={<ThemeSlot page="ExperienceDetailPage" />} />
          <Route path="/trip" element={<ThemeSlot page="ItineraryPage" />} />
        </Route>

        {/* Auth routes — pages resolved from the active theme (ADR-2026-07-D); pre-login, so
            these necessarily follow the platform-wide theme rather than a role/tier */}
        <Route element={<ThemeSlot page="AuthLayout" />}>
          <Route path="/auth/login" element={<ThemeSlot page="LoginPage" scope="auth" />} />
          <Route path="/auth/register" element={<ThemeSlot page="RegisterPage" scope="auth" />} />
          <Route path="/auth/forgot-password" element={<ThemeSlot page="ForgotPasswordPage" scope="auth" />} />
          <Route path="/auth/reset-password" element={<ThemeSlot page="ResetPasswordPage" scope="auth" />} />
          {/* Shared (non-themed) confirmation page for the email-verification link -- see
              VerifyEmailPage.tsx for why this one page isn't forked per-theme. */}
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          {/* Legacy routes - redirect to new paths */}
          <Route path="/login" element={<Navigate to="/auth/login" replace />} />
          <Route path="/register" element={<Navigate to="/auth/register" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/auth/forgot-password" replace />} />
          <Route path="/reset-password" element={<Navigate to="/auth/reset-password" replace />} />
        </Route>

        {/* Legal pages -- plain, non-themed (see TermsPage/PrivacyPage), linked from the Footer
            on every theme. Previously dead links (no route existed, hit the 404 catch-all). */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Dashboard redirect - intelligently routes users to their role-based dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRedirect />
            </ProtectedRoute>
          }
        />

        {/* Role-based dashboard routes (protected) */}
        <Route
          path="/dashboard/super-admin"
          element={
            <ProtectedRoute requiredRole="SUPER_ADMIN">
              <RoleBasedDashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:id" element={<StaffOrgDetailPage />} />
          <Route path="system-settings" element={<SystemSettingsPage />} />
          <Route path="feature-flags" element={<FeatureFlagsPage />} />
          <Route path="debug" element={<DebugPage />} />
          <Route path="backups" element={<BackupPage />} />
        </Route>

        <Route
          path="/dashboard/platform-admin"
          element={
            <ProtectedRoute requiredRole="PLATFORM_ADMIN">
              <RoleBasedDashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PlatformAdminDashboard2 />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:id" element={<StaffOrgDetailPage />} />
          <Route path="disputes" element={<DisputesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="payments" element={<PaymentPage />} />
          <Route path="compliance" element={<CompliancePagePlatform />} />
          <Route path="properties" element={<PropertiesPagePlatform />} />
          <Route path="bookings" element={<BookingsPagePlatform />} />
          <Route path="audit-log" element={<AuditLogPagePlatform />} />
          <Route path="reports" element={<ReportsPagePlatform />} />
          <Route path="settings" element={<Navigate to="/settings" replace />} />
          <Route path="listings" element={<ListingReviewPage />} />
          <Route path="experience-review" element={<ExperienceReviewPage />} />
          <Route path="platform-staff" element={<PlatformStaffPage />} />
          <Route path="appearance" element={<AppearancePagePlatform />} />
          <Route path="channel-managers" element={<ChannelManagerProvidersPage />} />
        </Route>

        {/* ORG_ADMIN — themed tier (ADR-2026-07-D). */}
        <Route
          path="/dashboard/org-admin"
          element={
            <ProtectedRoute requiredRole="ORG_ADMIN">
              <DashboardThemeSlot page="DashboardShell" />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardThemeSlot page="PropertyOverviewPage" />} />
          <Route path="properties" element={<DashboardThemeSlot page="PropertiesManagementPage" />} />
          <Route path="team" element={<DashboardThemeSlot page="TeamManagementPage" />} />
          <Route path="bookings" element={<DashboardThemeSlot page="BookingsManagementPage" />} />
          <Route path="bookings/:id" element={<BookingDetailPage />} />
          <Route path="guests" element={<DashboardThemeSlot page="GuestsDirectoryPage" />} />
          <Route path="reviews" element={<DashboardThemeSlot page="ReviewsManagementPage" />} />
          <Route path="analytics" element={<DashboardThemeSlot page="AnalyticsPage" />} />
          <Route path="settings" element={<DashboardThemeSlot page="ProfileSettingsPage" />} />
          <Route path="policies" element={<DashboardThemeSlot page="PoliciesPage" />} />
          <Route path="audit-log" element={<DashboardThemeSlot page="AuditLogPage" />} />
          <Route path="commission" element={<DashboardThemeSlot page="CommissionPage" />} />
          <Route path="payout-settings" element={<DashboardThemeSlot page="PayoutSettingsPage" />} />
          <Route path="calendar-sync" element={<DashboardThemeSlot page="CalendarSyncPage" />} />
          <Route path="channel-sync" element={<DashboardThemeSlot page="ChannelSyncPage" />} />
        </Route>

        {/* ORG_MANAGER (formerly PROPERTY_MANAGER) — themed tier (ADR-2026-07-D). */}
        <Route
          path="/dashboard/org-manager"
          element={
            <ProtectedRoute requiredRole="ORG_MANAGER">
              <DashboardThemeSlot page="DashboardShell" />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardThemeSlot page="PropertyOverviewPage" />} />
          <Route path="properties" element={<DashboardThemeSlot page="PropertiesManagementPage" />} />
          <Route path="bookings" element={<DashboardThemeSlot page="BookingsManagementPage" />} />
          <Route path="messages" element={<DashboardThemeSlot page="HostMessagesPage" />} />
          <Route path="analytics" element={<DashboardThemeSlot page="AnalyticsPage" />} />
          <Route path="guests" element={<DashboardThemeSlot page="GuestsDirectoryPage" />} />
          <Route path="compliance" element={<DashboardThemeSlot page="CompliancePage" />} />
          <Route path="settings" element={<DashboardThemeSlot page="ProfileSettingsPage" />} />
          <Route path="calendar-sync" element={<DashboardThemeSlot page="CalendarSyncPage" />} />
          <Route path="channel-sync" element={<DashboardThemeSlot page="ChannelSyncPage" />} />
          <Route path="bookings/:id" element={<BookingDetailPage />} />
        </Route>

        {/* ORG_STAFF (formerly STAFF) — themed tier (ADR-2026-07-D) */}
        <Route
          path="/dashboard/org-staff"
          element={
            <ProtectedRoute requiredRole="ORG_STAFF">
              <DashboardThemeSlot page="DashboardShell" />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardThemeSlot page="StaffOverviewPage" />} />
          <Route path="tasks" element={<DashboardThemeSlot page="StaffTasksPage" />} />
          <Route path="schedule" element={<DashboardThemeSlot page="StaffSchedulePage" />} />
          <Route path="properties" element={<DashboardThemeSlot page="PropertiesManagementPage" />} />
          <Route path="settings" element={<DashboardThemeSlot page="ProfileSettingsPage" />} />
        </Route>

        {/* GUEST — themed tier (ADR-2026-07-D) */}
        <Route
          path="/dashboard/guest"
          element={
            <ProtectedRoute requiredRole="GUEST">
              <DashboardThemeSlot page="DashboardShell" />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardThemeSlot page="GuestOverviewPage" />} />
          <Route path="explore" element={<ThemeSlot page="SearchResultsPage" />} />
          <Route path="bookings" element={<DashboardThemeSlot page="MyBookingsPage" />} />
          <Route path="bookings/:id" element={<BookingDetailPage />} />
          <Route path="trips" element={<DashboardThemeSlot page="MyTripsPage" />} />
          <Route path="trips/:id" element={<DashboardThemeSlot page="TripDetailPage" />} />
          <Route path="favorites" element={<DashboardThemeSlot page="FavoritesPage" />} />
          <Route path="messages" element={<DashboardThemeSlot page="GuestMessagesPage" />} />
          <Route path="reviews" element={<DashboardThemeSlot page="GuestReviewsPage" />} />
          <Route path="settings" element={<DashboardThemeSlot page="ProfileSettingsPage" />} />
        </Route>

        {/* PLATFORM_STAFF routes */}
        <Route
          path="/dashboard/platform-staff"
          element={
            <ProtectedRoute requiredRole="PLATFORM_STAFF">
              <RoleBasedDashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PlatformStaffDashboard />} />
          <Route path="organizations" element={<StaffOrganizationsPage />} />
          <Route path="organizations/:id" element={<StaffOrgDetailPage />} />
        </Route>

        {/* Settings routes (shared across all roles) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RoleBasedDashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SettingsPage />} />
          {/* Legacy path redirect */}
          <Route path="profile" element={<Navigate to="/settings" replace />} />
        </Route>

        {/* Property create route — themed tier (Org Admin/Org Manager) AND fixed tier (Super
            Admin/Platform Admin/Platform Staff creating on behalf of an org via ?organizationId=),
            ADR-2026-07-D. CreatePageShellSlot/CreatePageSlot pick the right shell/page per tier. */}
        <Route
          path="/dashboard/properties/new"
          element={
            <ProtectedRoute>
              <CreatePageShellSlot />
            </ProtectedRoute>
          }
        >
          <Route index element={<CreatePageSlot page="property" />} />
        </Route>
        <Route
          path="/dashboard/properties/:id/edit"
          element={
            <ProtectedRoute>
              <DashboardThemeSlot page="DashboardShell" />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardThemeSlot page="PropertyCreateEditPage" />} />
        </Route>

        {/* Experience create route — themed tier (Org Admin/Org Manager) AND fixed tier (Super
            Admin/Platform Admin/Platform Staff creating on behalf of an org), same pattern as
            the property route above. */}
        <Route
          path="/dashboard/experiences/new"
          element={
            <ProtectedRoute>
              <CreatePageShellSlot />
            </ProtectedRoute>
          }
        >
          <Route index element={<CreatePageSlot page="experience" />} />
        </Route>

        {/* Legacy Dashboard routes removed - using role-based dashboards instead */}

        {/* Admin routes — unauthenticated users redirected to login, authenticated go to platform-admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="PLATFORM_ADMIN">
              <Navigate to="/dashboard/platform-admin" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard/platform-admin" replace />
            </ProtectedRoute>
          }
        />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
